import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';

import { useDispatch } from 'store/configureStore';

import {
  deleteCoupon,
  deleteRedeemCashback,
} from 'actions/entities/orders/orders';
import { usePeertransferFlags } from 'hooks/usePeertransferFlags/usePeertransferFlags';
import { useCalculateRedeemCashback } from 'hooks/useRedeemCashback/useCalculateRedeemCashback';
import {
  getCouponCode,
  getOrder,
  isCouponApplied,
  isLoggedIn,
  isPayingFromChina,
  isPayingFromIndia,
  isRedeemCashbackApplied,
  shouldShowCoupon,
  shouldShowRedeemCashback,
} from 'selectors';

import { Coupon } from '../Coupon/Coupon';
import { RedeemCashback } from '../RedeemCashback/RedeemCashback';

const Discounts = ({ className }: { className?: string }) => {
  const dispatch = useDispatch();
  const { id: orderId, token } = useSelector(getOrder);
  const payingFromChina = useSelector(isPayingFromChina);
  const payingFromIndia = useSelector(isPayingFromIndia);
  const showCoupon = useSelector(shouldShowCoupon);
  const couponApplied = useSelector(isCouponApplied);
  const showRedeemCashback = useSelector(shouldShowRedeemCashback);
  const redeemCashbackApplied = useSelector(isRedeemCashbackApplied);
  const couponCode = useSelector(getCouponCode);
  const loggedIn = useSelector(isLoggedIn);

  const { data: calculateRedeemCashback } = useCalculateRedeemCashback(
    {
      orderId,
      token,
    },
    { enabled: showRedeemCashback },
  );

  const {
    availableRedeemAmount = 0,
    walletAmount = 0,
    bonusAmount,
  } = calculateRedeemCashback ?? {};

  const isRedeemCashbackEnabled =
    showRedeemCashback &&
    availableRedeemAmount > 0;

  const isCouponsEnabled =
    (payingFromChina || payingFromIndia) &&
    showCoupon &&
    !couponApplied &&
    !isRedeemCashbackEnabled;

  useEffect(() => {
    if (couponApplied) {
      dispatch(deleteCoupon(couponCode as string));
    }

    if (redeemCashbackApplied) {
      dispatch(deleteRedeemCashback());
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isRedeemCashbackEnabled && couponApplied) {
      dispatch(deleteCoupon(couponCode as string));
    }
  }, [couponApplied, isRedeemCashbackEnabled]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!loggedIn && redeemCashbackApplied) {
      dispatch(deleteRedeemCashback());
    }
  }, [loggedIn]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div data-testid="Discounts" className={className}>
      {isCouponsEnabled && <Coupon />}
      {isRedeemCashbackEnabled && (
        <RedeemCashback
          availableRedeemAmount={availableRedeemAmount}
          walletAmount={walletAmount}
          bonusAmount={bonusAmount}
        />
      )}
    </div>
  );
};

export { Discounts };
